import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type {
  EducationDetailDto,
  EnrollmentDto,
} from "../../types/client-education-detail.types";
import { EnrollmentStatus } from "../../types/client-education-detail.types";
import sectionStyles from "./sections.module.scss";

export function SurveySection(props: {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  onStart: (opts: { done: boolean }) => void;
}) {
  const { t } = useTranslation();
  const { education, enrollment, onStart } = props;
  if (!education.survey) return null;

  const done = !!enrollment.surveyCompletedAt;
  const canStart =
    !done &&
    (enrollment.status === EnrollmentStatus.EXAM_COMPLETED ||
      enrollment.status === EnrollmentStatus.SURVEY_STARTED ||
      (enrollment.status === EnrollmentStatus.CONTENT_COMPLETED &&
        !education.exam?.id));

  const buttonLabel = done
    ? t("surveySection.viewAnswers")
    : enrollment.status === EnrollmentStatus.SURVEY_STARTED
      ? t("surveySection.resume")
      : t("surveySection.start");

  const visualTone: "survey" | "passed" | "locked" = done
    ? "passed"
    : !canStart && education.exam?.id
      ? "locked"
      : "survey";
  const visualIcon = done ? "✅" : !canStart && education.exam?.id ? "🔒" : "📋";

  return (
    <section id="survey" className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <h2>{t("clientSurveySection.title")}</h2>
      </div>

      <div className={sectionStyles.moduleCard}>
        <div
          className={sectionStyles.moduleVisual}
          data-tone={visualTone}
          aria-hidden
        >
          <span className={sectionStyles.moduleVisualIcon}>{visualIcon}</span>
        </div>
        <div className={sectionStyles.moduleBody}>
          <div className={sectionStyles.moduleTitleRow}>
            <span className={sectionStyles.moduleName}>
              {t("clientSurveySection.subtitle")}
            </span>
          </div>

          {done && (
            <div className={sectionStyles.statusBanner} data-tone="success">
              <span className={sectionStyles.statusBannerIcon} aria-hidden>
                ✓
              </span>
              <span>
                {t("clientSurveySection.completedBadge")}
                {enrollment.surveyCompletedAt
                  ? ` · ${dayjs(enrollment.surveyCompletedAt).format("DD MMM YYYY")}`
                  : ""}
              </span>
            </div>
          )}

          {!done && !canStart && education.exam?.id && (
            <div className={sectionStyles.statusBanner} data-tone="info">
              <span className={sectionStyles.statusBannerIcon} aria-hidden>
                ℹ
              </span>
              <span>{t("clientSurveySection.lockedAfterExam")}</span>
            </div>
          )}

          {(canStart || done) && (
            <div className={sectionStyles.moduleActions}>
              <button
                type="button"
                onClick={() => onStart({ done })}
                className={
                  done
                    ? sectionStyles.moduleActionSecondary
                    : sectionStyles.moduleActionPrimary
                }
              >
                {buttonLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
